import styled from "styled-components";
import { TEXT_COLORS, PRIMARY_COLORS } from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import { TradeSyncStatus } from "../../services/tradeSync";

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
  font-size: ${FONT_SIZE.xs};
  color: ${TEXT_COLORS.three};
  white-space: nowrap;
  /* Sits directly before the "show market only" toggle in the accessory row. */
  margin-right: ${SPACING.two}px;
`;

const Warning = styled.span`
  color: #ff7a00;
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  color: ${PRIMARY_COLORS.one};
  cursor: pointer;
  text-decoration: underline;
`;

/** A list whose last read FAILED: whatever rows remain on screen are not current. */
export const LOAD_FAILED_STATUS: TradeSyncStatus = {
  state: "STALE",
  reason: "NETWORK",
};

/**
 * The status to show for a list. A failed read with older rows still on screen
 * is stale, whatever convergence says; with no rows the caller shows an error
 * state instead, so this stays out of the way.
 */
export const statusWithLoadError = (
  status: TradeSyncStatus,
  error: unknown,
  rowsOnScreen: number
): TradeSyncStatus => (error && rowsOnScreen > 0 ? LOAD_FAILED_STATUS : status);

interface SyncStatusIndicatorProps {
  status: TradeSyncStatus;
  onRetry: () => void;
  t: (key: string, options?: { defaultValue?: string }) => string;
}

/**
 * Tells the trader when this list could not be confirmed.
 *
 * Deliberately says nothing while data is confirmed current: a persistent badge
 * is noise on a trading screen. It appears only when a confirmed trade never
 * showed up in the server's answer within the retry budget — the case where the
 * rows on screen may be out of date and silence would mislead.
 */
export default function SyncStatusIndicator({
  status,
  onRetry,
  t,
}: SyncStatusIndicatorProps) {
  if (status.state !== "STALE") return null;

  return (
    <Row>
      <Warning>
        {status.reason === "NETWORK"
          ? t("sync_stale_network", {
              defaultValue: "Couldn't reach the server — data may be out of date.",
            })
          : t("sync_stale_not_converged", {
              defaultValue: "Couldn't confirm your last trade here — data may be out of date.",
            })}
      </Warning>
      <RetryButton type="button" onClick={onRetry}>
        {t("retry", { defaultValue: "Retry" })}
      </RetryButton>
    </Row>
  );
}
